/**
 * Playing-card helpers for Poker (moved out of PokerCardFace.tsx and
 * TvHud.tsx, 2026-09-26, unchanged) so the web page and the Apple TV app
 * (web/tv/appletv/) name, label, and evaluate cards with the same code.
 */
import type { TvGame } from './protocol';
import { describeHand, evaluateHand } from '../../../app/LagKing/src/multiplayerGames/pokerHands';
import type { PokerCard, PokerRank, PokerSuit } from '../../../app/LagKing/src/multiplayerGames/pokerDeck';

export type DisplayPokerCard = {
  rank?: string | number;
  suit?: string;
} | string;

const RANK_NAMES: Record<string, string> = {
  A: 'ace', K: 'king', Q: 'queen', J: 'jack',
  '14': 'ace', '13': 'king', '12': 'queen', '11': 'jack',
};
const SUIT_NAMES: Record<string, string> = {
  s: 'spades', '♠': 'spades', spade: 'spades', spades: 'spades',
  h: 'hearts', '♥': 'hearts', heart: 'hearts', hearts: 'hearts',
  d: 'diamonds', '♦': 'diamonds', diamond: 'diamonds', diamonds: 'diamonds',
  c: 'clubs', '♣': 'clubs', club: 'clubs', clubs: 'clubs',
};

function cardParts(card: DisplayPokerCard): { rank: string; suit: string } | null {
  if (typeof card !== 'string') {
    const rank = card.rank == null ? '' : String(card.rank).toUpperCase();
    const suit = card.suit == null ? '' : card.suit.toLowerCase();
    return rank && suit ? { rank, suit } : null;
  }
  const match = card.trim().match(/^(10|[2-9AJQK]|1[1-4])\s*([shdc♠♥♦♣]|spades?|hearts?|diamonds?|clubs?)$/i);
  return match ? { rank: match[1].toUpperCase(), suit: match[2].toLowerCase() } : null;
}

export function pokerCardLabel(card: DisplayPokerCard): string {
  const parts = cardParts(card);
  if (!parts) return typeof card === 'string' ? card : '';
  const rank = ({ '14': 'A', '13': 'K', '12': 'Q', '11': 'J' } as Record<string, string>)[parts.rank] ?? parts.rank;
  const suitName = SUIT_NAMES[parts.suit];
  const suit = ({ spades: '♠', hearts: '♥', diamonds: '♦', clubs: '♣' } as Record<string, string>)[suitName] ?? parts.suit;
  return `${rank}${suit}`;
}

/** The file name under public/cards/ (without .svg), e.g. "ace_spades". */
export function cardAssetKey(card: DisplayPokerCard): string | null {
  const parts = cardParts(card);
  if (!parts) return null;
  const rank = RANK_NAMES[parts.rank] ?? parts.rank;
  const suit = SUIT_NAMES[parts.suit];
  return suit ? `${rank}_${suit}` : null;
}

export function bestPokerCardLabels(cards: Extract<TvGame, { kind: 'poker' }>['activeHand']): Set<string> | null {
  if (cards.length <= 5) return null;
  const normalized = cards.map(normalizePokerCard).filter((card): card is PokerCard => card !== null);
  if (normalized.length !== cards.length) return null;
  return new Set(evaluateHand(normalized).cards.map((card) => pokerCardLabel(card)));
}

export function normalizePokerCard(card: Extract<TvGame, { kind: 'poker' }>['activeHand'][number]): PokerCard | null {
  const match = pokerCardLabel(card).match(/^(10|[2-9AJQK])([♠♥♦♣])$/);
  if (!match) return null;
  const ranks: Record<string, PokerRank> = { A: 14, K: 13, Q: 12, J: 11 };
  const suits: Record<string, PokerSuit> = { '♠': 's', '♥': 'h', '♦': 'd', '♣': 'c' };
  const rank = ranks[match[1]] ?? Number(match[1]) as PokerRank;
  return { rank, suit: suits[match[2]] };
}

export function formatPokerCard(
  card: Extract<TvGame, { kind: 'poker' }>['activeHand'][number],
): string {
  if (typeof card === 'string') return card;
  const rank = card.rank == null
    ? ''
    : ({ 14: 'A', 13: 'K', 12: 'Q', 11: 'J' } as Record<number, string>)[Number(card.rank)]
      ?? String(card.rank);
  const suit = card.suit == null
    ? ''
    : ({ s: '♠', h: '♥', d: '♦', c: '♣' } as Record<string, string>)[card.suit.toLowerCase()]
      ?? card.suit;
  return `${rank}${suit}`;
}

/** Seats with a name. Solo Poker (PokerSolo) publishes a blank second seat. */
export function pokerSeats(game: Extract<TvGame, { kind: 'poker' }>): Array<{ name: string; count: number }> {
  return game.names
    .map((name, index) => ({ name: typeof name === 'string' ? name.trim() : '', count: game.handCounts?.[index] ?? 0 }))
    .filter((seat) => seat.name.length > 0);
}

export function pokerBaseHand(
  hand: Extract<TvGame, { kind: 'poker' }>['activeHand'],
  award: Extract<TvGame, { kind: 'poker' }>['activeHand'],
): Extract<TvGame, { kind: 'poker' }>['activeHand'] {
  if (!award.length) return hand;
  const earned = new Set(award.map((card) => pokerCardLabel(card)));
  return hand.filter((card) => !earned.has(pokerCardLabel(card)));
}

/** The phone's named hand ("Pair of Kings"), from the app's evaluator. */
export function pokerHandName(cards: Extract<TvGame, { kind: 'poker' }>['activeHand']): string {
  const normalized = cards.map(normalizePokerCard).filter((card): card is PokerCard => card !== null);
  if (normalized.length === 0 || normalized.length !== cards.length) return '';
  return describeHand(evaluateHand(normalized)).toUpperCase();
}
